import { useMemo } from 'react'
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js'
import { Doughnut } from 'react-chartjs-2'
import type { ValueAttribution, Theme } from '@/types'
import { formatCurrency } from '@/utils/calculations'
import { getChartTheme } from '@/utils/chartConfig'

// Register Chart.js components
ChartJS.register(ArcElement, Tooltip, Legend)

interface DonutChartProps {
  attributions: ValueAttribution[]
  theme?: Theme
  showLegend?: boolean
  height?: number
}

export default function DonutChart({
  attributions,
  theme = 'dark',
  showLegend = false,
  height = 250,
}: DonutChartProps) {
  const chartTheme = useMemo(() => getChartTheme(theme), [theme])

  const data = useMemo(
    () => ({
      labels: attributions.map((a) => a.name),
      datasets: [
        {
          data: attributions.map((a) => a.attributedValue),
          backgroundColor: attributions.map((a) => a.color),
          borderColor: chartTheme.backgroundColor,
          borderWidth: 2,
          hoverOffset: 8,
        },
      ],
    }),
    [attributions, chartTheme.backgroundColor]
  )

  const options = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      cutout: '65%',
      plugins: {
        legend: {
          display: showLegend,
          position: 'right' as const,
          labels: {
            color: chartTheme.textColor,
            usePointStyle: true,
            padding: 16,
            font: {
              family: "'Source Sans 3', sans-serif",
              size: 12,
            },
          },
        },
        tooltip: {
          backgroundColor: chartTheme.tooltipBg,
          titleColor: chartTheme.tooltipText,
          bodyColor: chartTheme.tooltipText,
          borderColor: chartTheme.gridColor,
          borderWidth: 1,
          padding: 12,
          callbacks: {
            label: (ctx: { label?: string; raw: unknown }) =>
              `${ctx.label}: ${formatCurrency(ctx.raw as number)}`,
          },
        },
      },
    }),
    [chartTheme, showLegend]
  )

  const totalValue = attributions.reduce((sum, a) => sum + a.attributedValue, 0)

  return (
    <div className="relative" style={{ height }}>
      <Doughnut data={data} options={options} />
      {/* Center label */}
      <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
        <span className="text-sm text-brand-muted">Total Value</span>
        <span className="text-2xl font-semibold text-accent-teal">{formatCurrency(totalValue)}</span>
      </div>
    </div>
  )
}

/**
 * Legend component for donut chart (external)
 */
export function DonutLegend({ attributions }: { attributions: ValueAttribution[] }) {
  return (
    <div className="flex flex-wrap gap-3 mt-4">
      {attributions.map((attr) => (
        <div key={attr.id} className="flex items-center gap-2 text-sm">
          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: attr.color }} />
          <span className="text-brand-muted">{attr.name}</span>
          <span className="text-brand-light font-medium">{attr.percentOfTotal}%</span>
        </div>
      ))}
    </div>
  )
}
